/**
 * Voice transcription client — POSTs base64-encoded audio to
 * /api/voice/transcribe and returns the transcript. Matches the codebase
 * convention of JSON bodies over multipart (mirrors the feedback presigned
 * upload pattern, just inline since audio is small and ephemeral).
 */

export interface TranscribeArgs {
  blob: Blob;
  qrCode: string;
  language?: string;
  apiUrl: string;
}

export type TranscribeResult =
  | { ok: true; text: string; language: string }
  | { ok: false; error: string; code?: string };

/**
 * Base64-encode a binary Blob using FileReader. Avoids
 * `String.fromCharCode(...new Uint8Array(...))` which throws a stack
 * overflow on large arrays (recording > ~50 KB on some browsers).
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:audio/webm;base64,XXXXX..."
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

export async function transcribeAudio(args: TranscribeArgs): Promise<TranscribeResult> {
  let audio: string;
  try {
    audio = await blobToBase64(args.blob);
  } catch {
    return { ok: false, error: 'Could not encode audio' };
  }

  try {
    const res = await fetch(`${args.apiUrl}/api/voice/transcribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        qrCode: args.qrCode,
        language: args.language,
        audio,
      }),
    });
    const json = (await res.json().catch(() => null)) as {
      success?: boolean;
      data?: { text: string; language: string };
      error?: string;
      code?: string;
    } | null;
    if (!res.ok || !json?.success || !json.data) {
      return { ok: false, error: json?.error ?? `Request failed (${res.status})`, code: json?.code };
    }
    return { ok: true, text: json.data.text, language: json.data.language };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}
